import { LocaleLink } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import heroBg from "@/assets/hero-home-bg.jpg";
import Brand from "@/components/ui/brand";
import WhatIsCraftfindus from "@/components/seo/WhatIsCraftfindus";
import {
  ArrowRight,
  Shield,
  Users,
  Zap,
  ChevronDown,
  Search,
  Clock,
  BadgeCheck,
  HandCoins,
  Target,
  TrendingUp,
  Wrench,
  Paintbrush,
  Plug,
  Droplets,
  Sun,
  Hammer,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Index = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const trades = [
    { icon: Plug, title: t("home.electrician"), text: t("home.electricianDesc") },
    { icon: Droplets, title: t("home.plumbing"), text: t("home.plumbingDesc") },
    { icon: Hammer, title: t("home.roofing"), text: t("home.roofingDesc") },
    { icon: Paintbrush, title: t("home.painting"), text: t("home.paintingDesc") },
    { icon: Sun, title: t("home.solar"), text: t("home.solarDesc") },
    { icon: Wrench, title: t("home.construction"), text: t("home.constructionDesc") },
  ];

  const steps = [
    { step: "1", title: t("home.step1Title"), text: t("home.step1Desc") },
    { step: "2", title: t("home.step2Title"), text: t("home.step2Desc", { brand: "CRAFTFINDUS" }) },
    { step: "3", title: t("home.step3Title"), text: t("home.step3Desc") },
  ];

  const clientBenefits = [
    { icon: BadgeCheck, title: t("home.clientFree"), text: t("home.clientFreeDesc") },
    { icon: Search, title: t("home.clientNoJargon"), text: t("home.clientNoJargonDesc") },
    { icon: Clock, title: t("home.clientFast"), text: t("home.clientFastDesc") },
    { icon: Shield, title: t("home.clientQuality"), text: t("home.clientQualityDesc") },
  ];

  const providerBenefits = [
    { icon: Target, title: t("home.providerMatched"), text: t("home.providerMatchedDesc") },
    { icon: Users, title: t("home.providerCompetitors"), text: t("home.providerCompetitorsDesc") },
    { icon: HandCoins, title: t("home.providerTransparent"), text: t("home.providerTransparentDesc") },
    { icon: TrendingUp, title: t("home.providerRate"), text: t("home.providerRateDesc") },
  ];

  const principles = [
    { num: t("home.principle1Num"), icon: Zap, title: t("home.principle1Title"), text: t("home.principle1Desc") },
    { num: t("home.principle2Num"), icon: Users, title: t("home.principle2Title"), text: t("home.principle2Desc") },
    { num: t("home.principle3Num"), icon: Shield, title: t("home.principle3Title"), text: t("home.principle3Desc") },
  ];

  const stats = [
    { value: t("home.stat1Value"), label: t("home.stat1Label"), accent: true },
    { value: t("home.stat2Value"), label: t("home.stat2Label") },
    { value: t("home.stat3Value"), label: t("home.stat3Label") },
    { value: t("home.stat4Value"), label: t("home.stat4Label") },
  ];


  return (
    <div className="min-h-screen bg-background" id="main-content">
      <SEOHead
        canonical="/"
        title={t("home.seoTitle")}
        description={t("home.seoDesc")}
      />
      <JsonLd
        includeFaq
        includeService
        includeHowTo
        includeLocalBusiness
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden" aria-label={t("common.home")}>
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt={t("home.seoDesc")}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 section-padding pb-20 md:pb-24 pt-24 md:pt-32 w-full">
          <div className="max-w-5xl">
            <span className="text-micro opacity-0 animate-fade-up tracking-[0.15em] md:tracking-[0.2em]">
              {t("home.heroLabel")}
            </span>

            <h1 className="text-display mt-4 md:mt-6 opacity-0 animate-fade-up delay-100 leading-[0.95]">
              {t("home.heroTitle1")}<br className="hidden sm:block" />
              <span className="sm:hidden"> </span>{t("home.heroTitle2")}<br />
              <span className="text-accent">{t("home.heroTitle3")}</span>
            </h1>

            <p className="text-base md:text-lg text-muted-foreground max-w-xl mt-6 md:mt-8 opacity-0 animate-fade-up delay-200 leading-relaxed">
              {t("home.heroDesc")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 md:mt-12 opacity-0 animate-fade-up delay-300">
              <LocaleLink to="/inquiry" aria-label={t("home.heroCta")}>
                <Button variant="commit" size="lg" className="group w-full sm:w-auto">
                  {t("home.heroCta")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
              <LocaleLink to="/fuer-handwerker" aria-label={t("home.heroCtaCraft")}>
                <Button variant="quiet" size="lg" className="w-full sm:w-auto">
                  {t("home.heroCtaCraft")}
                </Button>
              </LocaleLink>
            </div>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-8 md:mt-10 opacity-0 animate-fade-up delay-400">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <BadgeCheck className="w-4 h-4 text-accent" aria-hidden="true" />
                {t("home.badge100Free")}
              </span>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4 text-accent" aria-hidden="true" />
                {t("home.badgeResponse")}
              </span>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-accent" aria-hidden="true" />
                {t("home.badgeVerified")}
              </span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 opacity-0 animate-fade-up delay-500 hidden md:block">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-muted-foreground/40 mx-auto" />
        </div>
      </section>

      {/* TRADES */}
      <section className="section-padding py-16 md:py-24" aria-label={t("home.tradesTitle")}>
        <div className="max-w-5xl mx-auto">
          <header className="text-center mb-10 md:mb-16">
            <span className="text-micro tracking-[0.2em] md:tracking-[0.3em] block mb-3 md:mb-4">{t("home.tradesLabel")}</span>
            <h2 className="text-headline">{t("home.tradesTitle")}</h2>
            <p className="text-caption mt-4 max-w-xl mx-auto">
              {t("home.tradesDesc", { brand: "CRAFTFINDUS" })}
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {trades.map((item) => (
              <article key={item.title} className="p-5 md:p-6 border border-border bg-card hover:border-accent/30 transition-colors duration-300">
                <item.icon className="w-6 h-6 text-accent mb-4" aria-hidden="true" />
                <h3 className="text-subheadline mb-2">{item.title}</h3>
                <p className="text-caption leading-relaxed">{item.text}</p>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <LocaleLink to="/inquiry">
              <Button variant="commit" size="lg" className="group">
                {t("home.tradesCta")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-padding py-16 md:py-28 bg-secondary/50" aria-label={t("home.howTitle")}>
        <div className="max-w-5xl mx-auto">
          <header className="text-center mb-12 md:mb-20">
            <span className="text-micro tracking-[0.3em] block mb-4">{t("home.howLabel")}</span>
            <h2 className="text-headline">{t("home.howTitle")}</h2>
            <p className="text-caption mt-4 max-w-xl mx-auto">{t("home.howDesc")}</p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
            {steps.map((item) => (
              <article key={item.step} className="relative p-6 md:p-8 border border-border bg-card hover:border-accent/30 transition-colors duration-300">
                <span
                  className="text-6xl font-medium text-accent/15 absolute top-4 right-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  aria-hidden="true"
                >
                  {item.step}
                </span>
                <div className="relative">
                  <h3 className="text-subheadline mb-3">{item.title}</h3>
                  <p className="text-caption leading-relaxed">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL VALUE PROPS */}
      <section className="section-padding py-16 md:py-28" aria-label={t("home.benefitsTitle")}>
        <div className="max-w-6xl mx-auto">
          <header className="text-center mb-10 md:mb-16">
            <span className="text-micro tracking-[0.2em] md:tracking-[0.3em] block mb-3 md:mb-4">{t("home.benefitsLabel")}</span>
            <h2 className="text-headline">{t("home.benefitsTitle")}</h2>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className="p-6 md:p-8 lg:p-10 border border-border bg-card flex flex-col">
              <span className="text-micro tracking-[0.3em] text-accent block mb-6">{t("home.forClients")}</span>
              <h3 className="text-headline mb-8">{t("home.clientsTitle")}</h3>
              <div className="space-y-6">
                {clientBenefits.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <item.icon className="w-5 h-5 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <h4 className="text-body font-medium mb-1">{item.title}</h4>
                      <p className="text-caption leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-8">
                <LocaleLink to="/inquiry">
                  <Button variant="commit" size="lg" className="group w-full sm:w-auto">
                    {t("home.clientCta")}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </LocaleLink>
              </div>
            </div>

            <div className="p-6 md:p-8 lg:p-10 border border-accent/20 bg-accent/[0.02] flex flex-col">
              <span className="text-micro tracking-[0.3em] text-accent block mb-6">{t("home.forProviders")}</span>
              <h3 className="text-headline mb-8">{t("home.providersTitle")}</h3>
              <div className="space-y-6">
                {providerBenefits.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <item.icon className="w-5 h-5 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <h4 className="text-body font-medium mb-1">{item.title}</h4>
                      <p className="text-caption leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-8">
                <LocaleLink to="/auth">
                  <Button variant="quiet" size="lg" className="group w-full sm:w-auto border-accent/30 hover:border-accent/60">
                    {t("home.providerCta")}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </LocaleLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section-padding py-16 md:py-28 bg-secondary/50" aria-label={t("home.principlesTitle", { brand: "CRAFTFINDUS" })}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 md:gap-4 mb-12 md:mb-20">
            <div className="h-px flex-1 bg-border" />
            <h2 className="text-micro tracking-[0.2em] md:tracking-[0.3em]">{t("home.principlesTitle", { brand: "CRAFTFINDUS" })}</h2>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {principles.map((item) => (
              <article key={item.num} className="group space-y-5">
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-accent" aria-hidden="true" />
                  <span className="text-micro">{item.num}</span>
                </div>
                <h3 className="text-headline">{item.title}</h3>
                <p className="text-caption leading-relaxed">{item.text}</p>
                <div className="h-px w-12 bg-accent/40 group-hover:w-full transition-all duration-500" />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="section-padding py-16 md:py-28" aria-label={t("home.statsTitle", { brand: "CRAFTFINDUS" })}>
        <div className="max-w-6xl mx-auto">
          <header className="text-center mb-10 md:mb-16">
            <h2 className="text-headline">{t("home.statsTitle", { brand: "CRAFTFINDUS" })}</h2>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center space-y-2 md:space-y-3 p-4 md:p-6">
                <p
                  className={`text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight ${stat.accent ? "text-accent" : "text-foreground"}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {stat.value}
                </p>
                <div className="h-px w-8 bg-border mx-auto" />
                <p className="text-micro">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST ARCHITECTURE */}
      <section className="section-padding py-16 md:py-28 bg-secondary/50" aria-label={t("home.trustArchTitle")}>
        <div className="max-w-6xl mx-auto">
          <header className="text-center mb-10 md:mb-16">
            <span className="text-micro tracking-[0.2em] md:tracking-[0.3em] block mb-3 md:mb-4">{t("home.trustArchLabel")}</span>
            <h2 className="text-headline">{t("home.trustArchTitle")}</h2>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              { icon: BadgeCheck, title: t("home.trustArch1Title"), desc: t("home.trustArch1Desc") },
              { icon: Target, title: t("home.trustArch2Title"), desc: t("home.trustArch2Desc") },
              { icon: Shield, title: t("home.trustArch3Title"), desc: t("home.trustArch3Desc") },
              { icon: HandCoins, title: t("home.trustArch4Title"), desc: t("home.trustArch4Desc") },
            ].map((item) => (
              <div key={item.title} className="p-6 md:p-8 border border-accent/20 bg-accent/[0.02]">
                <div className="flex items-center gap-3 mb-4">
                  <item.icon className="w-5 h-5 text-accent" aria-hidden="true" />
                  <h3 className="text-subheadline">{item.title}</h3>
                </div>
                <p className="text-caption leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <LocaleLink to="/warum-craftfindus">
              <Button variant="quiet" size="lg" className="group">
                {t("common.learnMore")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
          </div>
        </div>
      </section>


      {/* AI Search: Was ist CRAFTFINDUS? */}
      <WhatIsCraftfindus />

      {/* FAQ */}
      <section className="section-padding py-16 md:py-28 bg-secondary/50" aria-label={t("home.faqTitle")} id="faq">
        <div className="max-w-3xl mx-auto">
          <header className="text-center mb-10 md:mb-16">
            <span className="text-micro tracking-[0.2em] md:tracking-[0.3em] block mb-3 md:mb-4">{t("home.faqLabel")}</span>
            <h2 className="text-headline">{t("home.faqTitle")}</h2>
            <p className="text-caption mt-4">{t("home.faqDesc")}</p>
          </header>

          <div className="mb-8">
            <h3 className="text-micro tracking-[0.2em] mb-4 px-1">{t("home.faqForClients")}</h3>
            <div className="space-y-2">
              {([1,2,3,4] as const).map((i) => (
                <FaqAccordion key={`c${i}`} question={t(`home.faqC${i}Q`)} answer={t(`home.faqC${i}A`)} />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-micro tracking-[0.2em] mb-4 px-1">{t("home.faqForProviders")}</h3>
            <div className="space-y-2">
              {([1,2,3,4] as const).map((i) => (
                <FaqAccordion key={`p${i}`} question={t(`home.faqP${i}Q`)} answer={t(`home.faqP${i}A`)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BANNER */}
      <section className="section-padding py-10 md:py-14 border-t border-border" aria-label="Social Proof">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <p className="text-caption text-muted-foreground">
            {t("home.socialProof")}
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-accent" />{t("home.proofVerified")}</span>
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-accent" />{t("home.proofDsgvo")}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-accent" />{t("home.proofResponse")}</span>
          </div>
        </div>
      </section>

      {/* DUAL CTA */}
      <section className="relative section-padding py-16 md:py-28 overflow-hidden" aria-label={t("home.ctaLabel")}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

        <div className="relative max-w-4xl mx-auto">
          <header className="text-center mb-12">
            <span className="text-micro tracking-[0.3em] block mb-4">{t("home.ctaLabel")}</span>
            <h2 className="text-headline">{t("home.ctaTitle")}</h2>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 border border-border bg-card text-center space-y-5 hover:border-accent/30 transition-colors">
              <Search className="w-8 h-8 text-accent mx-auto" aria-hidden="true" />
              <h3 className="text-subheadline">{t("home.ctaClientTitle")}</h3>
              <p className="text-caption leading-relaxed">{t("home.ctaClientDesc")}</p>
              <LocaleLink to="/inquiry" className="block mt-8">
                <Button variant="commit" size="lg" className="group w-full">
                  {t("home.ctaClientBtn")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>

            <div className="p-8 border border-accent/20 bg-accent/[0.02] text-center space-y-5 hover:border-accent/40 transition-colors">
              <Target className="w-8 h-8 text-accent mx-auto" aria-hidden="true" />
              <h3 className="text-subheadline">{t("home.ctaProviderTitle")}</h3>
              <p className="text-caption leading-relaxed">{t("home.ctaProviderDesc")}</p>
              <LocaleLink to="/auth" className="block mt-8">
                <Button variant="quiet" size="lg" className="group w-full border-accent/30 hover:border-accent/60">
                  {t("home.ctaProviderBtn")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

function FaqAccordion({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary/30 transition-colors"
        aria-expanded={open}
      >
        <h3 className="text-body font-medium pr-4">{question}</h3>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-caption leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default Index;
